// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0

import {
  ActionIcon,
  Badge,
  Button,
  Group,
  Loader,
  Modal,
  Paper,
  Progress,
  Stack,
  Text,
  Tooltip,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { getDisplayString } from '@medplum/core';
import {
  IconGripVertical,
  IconMicrophone,
  IconPlayerPause,
  IconPlayerPlay,
  IconPlayerStop,
  IconX,
} from '@tabler/icons-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { JSX, MouseEvent as ReactMouseEvent, TouchEvent as ReactTouchEvent } from 'react';
import { useNavigate } from 'react-router';
import { useAIRecording } from '../contexts/AIRecordingContext';
import classes from './FloatingRecorder.module.css';

function formatDuration(ms: number): string {
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
}

export function FloatingRecorder(): JSX.Element | null {
  const navigate = useNavigate();
  const {
    session,
    stopRecording,
    pauseRecording,
    resumeRecording,
    cancelRecording,
  } = useAIRecording();

  const [confirmModalOpened, { open: openConfirmModal, close: closeConfirmModal }] = useDisclosure(false);

  // Dragging state
  const [position, setPosition] = useState({ x: 20, y: 20 }); // bottom-right offset
  const [isDragging, setIsDragging] = useState(false);
  const dragStartRef = useRef({ x: 0, y: 0 });
  const positionStartRef = useRef({ x: 0, y: 0 });

  const handleMouseDown = useCallback((e: ReactMouseEvent) => {
    // Only start drag from the grip handle
    if ((e.target as HTMLElement).closest('[data-drag-handle]')) {
      e.preventDefault();
      setIsDragging(true);
      dragStartRef.current = { x: e.clientX, y: e.clientY };
      positionStartRef.current = { ...position };
    }
  }, [position]);

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!isDragging) return;

    const deltaX = dragStartRef.current.x - e.clientX;
    const deltaY = dragStartRef.current.y - e.clientY;

    // Calculate new position (we're using bottom-right positioning)
    const newX = Math.max(0, Math.min(window.innerWidth - 300, positionStartRef.current.x + deltaX));
    const newY = Math.max(0, Math.min(window.innerHeight - 200, positionStartRef.current.y + deltaY));

    setPosition({ x: newX, y: newY });
  }, [isDragging]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  // Touch handlers for mobile
  const handleTouchStart = useCallback((e: ReactTouchEvent) => {
    if ((e.target as HTMLElement).closest('[data-drag-handle]')) {
      const touch = e.touches[0];
      setIsDragging(true);
      dragStartRef.current = { x: touch.clientX, y: touch.clientY };
      positionStartRef.current = { ...position };
    }
  }, [position]);

  const handleTouchMove = useCallback((e: TouchEvent) => {
    if (!isDragging) return;
    e.preventDefault();

    const touch = e.touches[0];
    const deltaX = dragStartRef.current.x - touch.clientX;
    const deltaY = dragStartRef.current.y - touch.clientY;

    const newX = Math.max(0, Math.min(window.innerWidth - 300, positionStartRef.current.x + deltaX));
    const newY = Math.max(0, Math.min(window.innerHeight - 200, positionStartRef.current.y + deltaY));

    setPosition({ x: newX, y: newY });
  }, [isDragging]);

  // Attach/detach global mouse/touch listeners
  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      window.addEventListener('touchmove', handleTouchMove, { passive: false });
      window.addEventListener('touchend', handleMouseUp);
    }

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', handleMouseUp);
    };
  }, [isDragging, handleMouseMove, handleMouseUp, handleTouchMove]);

  const handleCancelClick = useCallback(() => {
    openConfirmModal();
  }, [openConfirmModal]);

  const confirmCancel = useCallback(() => {
    cancelRecording();
    closeConfirmModal();
  }, [cancelRecording, closeConfirmModal]);

  const handleNavigateToEncounter = useCallback(() => {
    if (session?.encounter?.id && session?.patient?.id) {
      navigate(`/Patient/${session.patient.id}/Encounter/${session.encounter.id}`);
    }
  }, [navigate, session?.encounter?.id, session?.patient?.id]);

  // Don't render if no active session
  if (!session) {
    return null;
  }

  const isRecording = session.state === 'recording';
  const isPaused = session.state === 'paused';
  const isProcessing = session.state === 'processing' || session.state === 'transcribing';

  const patientName = session.patient ? getDisplayString(session.patient) : 'Patient';

  return (
    <>
      <Paper
        className={`${classes.floatingRecorder} ${isDragging ? classes.dragging : ''}`}
        shadow="lg"
        withBorder
        style={{
          right: position.x,
          bottom: position.y,
        }}
        onMouseDown={handleMouseDown}
        onTouchStart={handleTouchStart}
      >
        <Stack gap="xs">
          {/* Drag handle */}
          <Group justify="space-between" wrap="nowrap">
            <Group gap="xs" wrap="nowrap">
              <div data-drag-handle className={classes.dragHandle}>
                <IconGripVertical size={14} />
              </div>
              <div className={`${classes.recordingDot} ${isRecording ? classes.active : ''}`} />
              <Text size="sm" fw={600} truncate style={{ maxWidth: 150 }}>
                {patientName}
              </Text>
              {isRecording && (
                <Badge size="xs" color="red" variant="filled">
                  REC
                </Badge>
              )}
              {isPaused && (
                <Badge size="xs" color="yellow" variant="filled">
                  PAUSED
                </Badge>
              )}
              {isProcessing && (
                <Badge size="xs" color="blue" variant="filled">
                  PROCESSING
                </Badge>
              )}
            </Group>
            {!isProcessing && (
              <Tooltip label="Cancel recording">
                <ActionIcon
                  variant="subtle"
                  color="red"
                  size="sm"
                  onClick={handleCancelClick}
                >
                  <IconX size={14} />
                </ActionIcon>
              </Tooltip>
            )}
          </Group>

          {/* Duration and audio level */}
          <Group gap="sm" wrap="nowrap">
            <IconMicrophone size={16} className={isRecording ? classes.micActive : classes.micInactive} />
            <Text size="lg" fw={600} ff="monospace">
              {formatDuration(session.durationMs)}
            </Text>
            {isRecording && (
              <Progress
                value={session.audioLevel}
                size="sm"
                color="violet"
                className={classes.audioLevel}
                animated
              />
            )}
          </Group>

          {/* Status message */}
          {session.statusMessage && (
            <Group gap="xs" wrap="nowrap">
              {isProcessing && <Loader size="xs" />}
              <Text size="xs" c="dimmed">
                {session.statusMessage}
              </Text>
            </Group>
          )}

          {/* Controls */}
          {!isProcessing && (
            <Group gap="xs" justify="center">
              {/* Pause/Resume */}
              <Tooltip label={isPaused ? 'Resume' : 'Pause'}>
                <ActionIcon
                  variant="light"
                  color="gray"
                  size="lg"
                  onClick={isPaused ? resumeRecording : pauseRecording}
                >
                  {isPaused ? <IconPlayerPlay size={18} /> : <IconPlayerPause size={18} />}
                </ActionIcon>
              </Tooltip>

              {/* Stop and process */}
              <Tooltip label="Stop and generate notes">
                <ActionIcon
                  variant="filled"
                  color="violet"
                  size="lg"
                  onClick={stopRecording}
                >
                  <IconPlayerStop size={18} />
                </ActionIcon>
              </Tooltip>

              {/* Navigate to encounter */}
              {session.patient?.id && (
                <Tooltip label="Go to visit">
                  <Button
                    variant="subtle"
                    size="xs"
                    onClick={handleNavigateToEncounter}
                  >
                    View Visit
                  </Button>
                </Tooltip>
              )}
            </Group>
          )}

          {/* Error message */}
          {session.error && (
            <Text size="xs" c="red">
              {session.error}
            </Text>
          )}
        </Stack>
      </Paper>

      {/* Cancel confirmation modal */}
      <Modal
        opened={confirmModalOpened}
        onClose={closeConfirmModal}
        title="Cancel Recording?"
        centered
        size="sm"
      >
        <Stack>
          <Text size="sm">
            Are you sure you want to cancel this recording? The audio will not be saved and no notes will be generated.
          </Text>
          <Group justify="flex-end" gap="sm">
            <Button variant="default" onClick={closeConfirmModal}>
              Continue Recording
            </Button>
            <Button color="red" onClick={confirmCancel}>
              Cancel Recording
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
}
